import React, { useContext, useEffect, useMemo, useState } from "react";
import { PDFDesignerProviderValue, PhoenixPDFsDesignerContext } from "./PhoenixPdfDesignerContext";
import { PdfDisplayControl } from "interfaces/Phoenix.FormsDesigner.Models";
import { classes } from "components/Utility";
import IconButton from "components/Shared/IconButton";
import { Button } from "components/Shared";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";
import { stopPropagationPreventDefaultNoFn } from "tools/clickEvents";
import DesignerPdfControl, { reorderLabelHandle } from "./DesignerPdfControl";
import TextField from "@mui/material/TextField";

const reorderSectionHandle = "reorder-section-handle";

interface DesignerElementProps {
  element: PdfDisplayControl;
  elementIndex: number;
  isCollapsed: boolean;
  setCollapsed: (newValue: boolean) => void;
  setAllCollapsed: (newValue: boolean) => void;
  areAllCollapsed: boolean;
}

const PhoenixPdfDesignerSection = ({ element, isCollapsed, setCollapsed, setAllCollapsed, areAllCollapsed }: DesignerElementProps) => {
  let context = useContext<PDFDesignerProviderValue>(PhoenixPDFsDesignerContext);
  const elementIndex = context.pdfDefinition.displayControls.indexOf(element);
  const [newElementTitle, setNewElementTitle] = useState<string>();
  const [isRenamingElement, setIsRenamingElement] = useState<boolean>();

  const controls = useMemo<ReorderableItem[]>(
    () => (element.children ?? []).map((label) => {
      return {
        id: label.id,
        node: <DesignerPdfControl
          key={label.id}
          label={label}
          parentSection={element}
          className={(!context.isShowingHidden && label.properties.hide) && "hide-element"}
        />,
      };
    }),
    [context.pdfDefinitionUpdatedAt, context.isShowingHidden]);

  const editElementName = () => {
    setIsRenamingElement(true);
    setNewElementTitle(element.name);
  };

  const cancelEditingElementName = () => {
    setIsRenamingElement(false);
  };

  const saveElementName = () => {
    context.editFns.renameChildren(elementIndex, newElementTitle);
    cancelEditingElementName();
  };

  let titleNode = (
    <>
      <IconButton icon="edit" onClick={editElementName} />
      <span>{element.name}</span>
    </>
  );

  if (isRenamingElement) {
    titleNode = (
      <>
        <IconButton icon="clear" onClick={cancelEditingElementName} />
        <IconButton icon="check" onClick={saveElementName} />
        <span><TextField variant="standard" value={newElementTitle} onChange={(e) => setNewElementTitle(e.target.value)} /></span>
      </>
    );
  }

  return (
    <div className="form-section">
      <div className="section-title">
        {areAllCollapsed && <IconButton icon="reorder" className={reorderSectionHandle} onClick={stopPropagationPreventDefaultNoFn} />}
        <IconButton icon="keyboard_arrow_down" className={classes("md-collapser", !isCollapsed && "md-collapser--flipped")} onClick={() => setCollapsed(!isCollapsed)} />
        <IconButton icon="keyboard_double_arrow_down" className={classes("md-collapser", !isCollapsed && "md-collapser--flipped")} onClick={() => setAllCollapsed(!isCollapsed)} />
        {titleNode}
        <IconButton icon="delete" onClick={() => context.editFns.deleteChildren(elementIndex)} />
      </div>
      {!isCollapsed && <>
        <ReorderableGrid
          className="section-controls"
          items={controls}
          handleSelector={`.${reorderLabelHandle}`}
          onReorder={ids => context.editFns.reorderChildren(element, ids)}
        />
        <div className="new-question-container">
          <Button
            primary
            onClick={() => context.editFns.addNewLabel({control: element})}
          >
            Add New Label
          </Button>
          <Button
            primary
            onClick={() => context.editFns.addNewSection(elementIndex)}
          >
            Add New Section
          </Button>
        </div>
      </>}
    </div>
  );
};

export default PhoenixPdfDesignerSection;
